import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@saas-kit/lib';
import { Drawer, ErrorBanner, Spinner } from '@saas-kit/ui';

import { type InvoiceRecord, type PaymentRecord, getInvoice } from './api';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';

interface Props {
  invoice: InvoiceRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatMoney(amount: string, currency: string): string {
  return `${amount} ${currency}`;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

export function InvoiceDetailDrawer({
  invoice,
  open,
  onOpenChange,
}: Props): JSX.Element {
  const { t } = useTranslation('billing');
  const selectedTenant = useAuth((s) => s.selectedTenant);

  const detailQuery = useQuery({
    queryKey: [
      'tenant-billing-invoice',
      selectedTenant?.tenant_id,
      invoice.id,
    ],
    queryFn: () => getInvoice(invoice.id),
    enabled: open && Boolean(selectedTenant?.tenant_id),
  });

  const current = detailQuery.data?.invoice ?? invoice;
  const payments = detailQuery.data?.payments ?? [];
  const outstanding = Number(current.total_amount) - Number(current.amount_paid);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('detail.title', { number: current.invoice_number })}
      testId="tenant-invoice-detail"
      width="lg"
    >
      {detailQuery.isError ? (
        <ErrorBanner>{t('errors.load_failed')}</ErrorBanner>
      ) : null}
      {detailQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <InvoiceStatusBadge status={current.status} />
            <span className="text-sm text-muted-foreground">
              {t('detail.outstanding')}: {outstanding > 0 ? outstanding.toFixed(2) : '0.00'} {current.currency}
            </span>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">{t('detail.total')}</dt>
            <dd className="font-medium">
              {formatMoney(current.total_amount, current.currency)}
            </dd>
            <dt className="text-muted-foreground">{t('detail.subtotal')}</dt>
            <dd>{formatMoney(current.subtotal_amount, current.currency)}</dd>
            <dt className="text-muted-foreground">{t('detail.tax')}</dt>
            <dd>{formatMoney(current.tax_amount, current.currency)}</dd>
            <dt className="text-muted-foreground">{t('detail.amount_paid')}</dt>
            <dd>{formatMoney(current.amount_paid, current.currency)}</dd>
            <dt className="text-muted-foreground">{t('detail.issued_at')}</dt>
            <dd>{formatDate(current.issued_at)}</dd>
            <dt className="text-muted-foreground">{t('detail.due_at')}</dt>
            <dd>{formatDate(current.due_at)}</dd>
            <dt className="text-muted-foreground">{t('detail.paid_at')}</dt>
            <dd>{formatDate(current.paid_at)}</dd>
          </dl>
          {current.notes ? (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
                {t('detail.notes')}
              </p>
              {current.notes}
            </div>
          ) : null}
          <PaymentsList payments={payments} currency={current.currency} />
        </div>
      )}
    </Drawer>
  );
}

function PaymentsList({
  payments,
  currency,
}: {
  payments: PaymentRecord[];
  currency: string;
}): JSX.Element {
  const { t } = useTranslation('billing');
  if (payments.length === 0) {
    return (
      <section>
        <h3 className="mb-2 text-sm font-medium">{t('detail.payments')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('detail.no_payments')}
        </p>
      </section>
    );
  }
  return (
    <section>
      <h3 className="mb-2 text-sm font-medium">{t('detail.payments')}</h3>
      <ul className="divide-y divide-border rounded-md border border-border">
        {payments.map((payment) => (
          <li
            key={payment.id}
            className="flex items-center justify-between px-3 py-2"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {formatMoney(payment.amount, currency)}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(`detail.methods.${payment.payment_method}`)} ·{' '}
                {formatDate(payment.received_at)}
                {payment.reference ? ` · ${payment.reference}` : ''}
              </p>
            </div>
            {payment.status === 'void' ? (
              <span className="text-xs text-muted-foreground">
                {t('detail.voided')}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
